import { useState } from 'react';
import { FundingPolicyType } from '@shared';
import { deriveApplicationStatus } from '@/pages/PolicyApplicationDashboard';

export function useFundingSourceEditGuard(
  policyApplications: ClientPolicyApplication[],
) {
  const [showEditBlockedDialog, setShowEditBlockedDialog] = useState(false);
  const [editBlockedReason, setEditBlockedReason] = useState<string>('');

  const checkCanEditFundingSource = (
    fundingSourceId: string,
    policyType: FundingPolicyType | null | undefined,
  ): { canEdit: boolean; reason?: string } => {
    if (policyType !== FundingPolicyType.careScoutCareAssurance) {
      return { canEdit: true };
    }

    const linkedApplications = policyApplications.filter(
      app => app.fundingSourceId === fundingSourceId,
    );

    for (const application of linkedApplications) {
      const { status } = deriveApplicationStatus(application);
      if (
        status === 'in_progress' ||
        status === 'submitted' ||
        status === 'completed'
      ) {
        return {
          canEdit: false,
          reason:
            'Cannot edit this funding source because there is an active application attached to it. To make changes, please clear the application progress first, then edit the funding source.',
        };
      }
    }

    return { canEdit: true };
  };

  const guardedEdit = (
    fundingSourceId: string | undefined,
    policyType: FundingPolicyType | null | undefined,
    openModal: (open: boolean) => void,
  ) => {
    if (!fundingSourceId) {
      openModal(true);
      return;
    }

    const { canEdit, reason } = checkCanEditFundingSource(fundingSourceId, policyType);
    if (!canEdit) {
      setEditBlockedReason(reason || 'This funding source cannot be edited.');
      setShowEditBlockedDialog(true);
      return;
    }

    openModal(true);
  };

  return {
    showEditBlockedDialog,
    setShowEditBlockedDialog,
    editBlockedReason,
    guardedEdit,
  };
}
